import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowUpRight, Bot, CalendarClock, ChevronDown, ChevronUp, FileText,
  Folder, Hash, Loader2, Mail, MessageSquareText, Mic, Paperclip, Pencil, Terminal, Upload,
} from 'lucide-react';
import type { AutomationTrigger } from '@repo/shared-types';
import { Layout } from '../components/layout/Layout';
import { ProjectMark } from '../components/project/ProjectMark';
import { ProjectStarter } from '../components/project/ProjectStarter';
import { Badge } from '../components/ui/badge';
import { BreathingDot } from '../components/ui/breathing';
import { Card } from '../components/ui/card';
import {
  api,
  type AutomationWithRuns,
  type FeedSourceType,
  type KbGivenPart,
  type KbStats,
  type KbTree,
  type KbTreeNode,
  type KbUpload,
} from '../lib/api';
import { PlacementMark } from '../components/run/RunPlacement';
import { useCurrentProject } from '../lib/project';
import { formatChars } from '../lib/runs';
import { PartText } from '../components/run/PartText';
import { formatRunLabel, formatSource, useSourceLabels } from '../lib/source-label';
import { cn, timeAgo } from '../lib/utils';
import { triggerIcon, triggerSummary } from './Automations';

/**
 * Project overview — the landing view for `/p/:projectSlug`, and the one page that answers "what
 * is going on in this project" without having to visit six others.
 *
 * Every section reads the API. They fetch independently rather than through one aggregate
 * endpoint: they hit unrelated tables at very different speeds (the KB tree is a single indexed
 * read, integrations talk to Slack), so one slow source would otherwise hold up the whole page,
 * and one failing source would blank it. A section that fails says so in place and leaves the
 * rest intact.
 *
 * Ordered by how often it answers a question: what it knows, what it has been doing, what came in,
 * and what runs on a schedule. Asking the knowledge base is not here — it reads across the whole
 * hierarchy and has its own page (Ask KB), and a second search box scoped to one project only
 * invited the question of why its answers differed from that page's.
 *
 * What *feeds* the project is deliberately not here. It used to lead the page, and it was the one
 * section that raised more questions than it answered: a pinned source and a globally-watched one
 * make different promises about where content lands, and a list mixing them — with a `routed`
 * badge to carry the distinction — read as though everything on it fed this project. The two
 * places that own that answer say it unambiguously: the project's own Settings → Integrations for
 * what is pinned here, and Sources for what the organization watches.
 */

// ─── Building blocks ───────────────────────────────────────────────────────

/** Collapsible titled panel. Every block on this page is one of these. */
function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);

  return (
    <Card className="overflow-hidden">
      <div className={cn('flex items-center gap-2 px-4 py-2.5', open && 'border-b border-border-subtle')}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint hover:text-ink-muted transition-colors"
        >
          {title}
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </button>
        {action && <div className="ml-auto">{action}</div>}
      </div>
      {open && <div className="divide-y divide-border-subtle">{children}</div>}
    </Card>
  );
}

function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex items-center gap-2 px-4 py-2.5 text-sm text-ink', className)}>{children}</div>;
}

/** The source a row came from — Granola, Slack, an upload. */
function SourceChip({ icon: Icon, label }: { icon: typeof Mic; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-border bg-surface-subtle text-[10px] font-medium text-ink-muted flex-shrink-0">
      <Icon size={10} />
      {label}
    </span>
  );
}

function Meta({ children }: { children: ReactNode }) {
  return <span className="ml-auto text-xs text-ink-faint whitespace-nowrap flex-shrink-0">{children}</span>;
}

/** A section's header action — always a link now that every section has somewhere to go. */
function HeaderLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink text-card text-[11px] font-medium hover:opacity-85 transition-opacity"
    >
      {children}
    </Link>
  );
}

/**
 * What a section shows instead of rows: loading, the reason it failed, or that there is genuinely
 * nothing yet. Kept as one component so an empty project and a broken endpoint never look alike.
 */
function Placeholder({
  loading,
  error,
  empty,
}: {
  loading: boolean;
  error: string | null;
  empty: string;
}) {
  if (error) {
    return (
      <Row className="text-xs text-danger-text">
        <AlertTriangle size={12} className="flex-shrink-0" />
        {error}
      </Row>
    );
  }
  if (loading) {
    return (
      <Row className="text-xs text-ink-faint">
        <Loader2 size={12} className="animate-spin flex-shrink-0" />
        Loading…
      </Row>
    );
  }
  return <Row className="text-xs text-ink-faint">{empty}</Row>;
}

/**
 * One project-scoped fetch, re-run when the project changes.
 *
 * `load` must be stable across renders — the fetchers below are module-level constants for that
 * reason. The `current` flag drops a response for a project that is no longer on screen: this page
 * stays mounted while the sidebar switches projects, so a slow response for the old one would
 * otherwise overwrite the new one's data.
 */
function useProjectResource<T>(projectId: string | undefined, load: (id: string) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    if (!projectId) return;

    let current = true;
    setLoading(true);

    load(projectId)
      .then((result) => {
        if (!current) return;
        setData(result);
        setError(null);
      })
      .catch((e: Error) => { if (current) setError(e.message); })
      .finally(() => { if (current) setLoading(false); });

    return () => { current = false; };
  }, [projectId, load, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);
  return { data, error, loading, reload };
}

const loadStats = (id: string) => api.kb.stats(id);
const loadTree = (id: string) => api.kb.tree(id);
/**
 * This project's ingestion activity.
 *
 * `scope: 'self'` because a parent's runs are not events in this project — but with
 * `includeDestinations`, a run that was *read* from the organization root and wrote here counts as
 * this project's activity, which is the only way routed content is visible where it landed. Without
 * it a project could gain three pages from a meeting and show an empty Activity section, while the
 * root showed a run whose work happened two levels down.
 */
const loadAbsorbed = (id: string) => api.kb.absorbedItems(id, 12);
const loadUploads = (id: string) => api.kb.uploads(id, 8);
const loadAutomations = (id: string) => api.automations.list(id);

/** Icon per ingestion source, so a row's origin reads at a glance. */
const SOURCE_ICONS: Record<FeedSourceType, typeof Mic> = {
  GRANOLA: Mic,
  SLACK: Hash,
  FILE_UPLOAD: Upload,
  CLI_IMPORT: Terminal,
  MANUAL: FileText,
  MCP: Bot,
  USER_EDIT: Pencil,
  USER_FEEDBACK: MessageSquareText,
  // A mail attachment and a Slack-linked file are both LINKED_DOCUMENT: a paperclip reads as
  // "came with something else", which is exactly what distinguishes it from a deliberate upload.
  LINKED_DOCUMENT: Paperclip,
  EMAIL: Mail,
  DRIVE_FILE: FileText,
  // A person's correction to an automation's document — a pencil, like any other hand edit.
  AUTOMATION_EDIT: Pencil,
};

// ─── Headline numbers ──────────────────────────────────────────────────────

interface StatTile {
  key: string;
  value: string;
  label: string;
  /** Project-relative route this number is accountable to, if there is one to open. */
  to?: string;
}

/**
 * The four tiles, from one stats response.
 *
 * `null` covers both loading and failure: the tiles keep their shape and show an em dash rather
 * than a zero, because "0 pages" and "we couldn't ask" are very different statements to put in
 * front of someone and a spinner-shaped layout shift is worse than either.
 */
function statTiles(stats: KbStats | null): StatTile[] {
  return [
    {
      key: 'pages',
      value: stats ? String(stats.pageCount) : '—',
      label: 'pages in memory',
    },
    {
      key: 'pending',
      value: stats ? String(stats.pendingChanges) : '—',
      label: 'need your OK',
      to: 'kb-review',
    },
    {
      key: 'velocity',
      value: stats ? `${stats.recentChanges}/wk` : '—',
      label: 'velocity',
    },
    {
      key: 'activity',
      // A knowledge base nobody has written to yet has no timestamp to relativise, and "just
      // now" would be a lie about an empty project.
      value: stats ? (stats.lastActivityAt ? timeAgo(stats.lastActivityAt) : 'never') : '—',
      label: 'last activity',
    },
  ];
}

/**
 * Four across, folding to 2×2 on narrow screens — hence the per-tile dividers rather than
 * `divide-x`, which would draw a border down the left edge of the second row.
 */
function StatsRow({ projectId, projectSlug }: { projectId: string | undefined; projectSlug: string }) {
  const { data: stats, error } = useProjectResource(projectId, loadStats);

  return (
    <>
      <Card className="grid grid-cols-2 sm:grid-cols-4 mb-4 overflow-hidden">
        {statTiles(stats).map((stat, i) => (
          <div
            key={stat.key}
            className={cn(
              'px-5 py-4 border-border-subtle',
              i % 2 === 1 && 'border-l',
              i < 2 && 'border-b sm:border-b-0',
              'sm:border-l',
              i === 0 && 'sm:border-l-0',
            )}
          >
            <p className="text-2xl font-bold text-ink leading-none">{stat.value}</p>
            {stat.to ? (
              <Link
                to={`/p/${projectSlug}/${stat.to}`}
                className="mt-1.5 inline-flex items-center gap-0.5 text-xs text-orange-500 hover:text-orange-600 transition-colors"
              >
                {stat.label}
                <ArrowUpRight size={11} />
              </Link>
            ) : (
              <p className="mt-1.5 text-xs text-ink-muted">{stat.label}</p>
            )}
          </div>
        ))}
      </Card>

      {error && (
        <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2">
          Couldn’t load these numbers: {error}
        </p>
      )}
    </>
  );
}

// ─── Knowledge base ────────────────────────────────────────────────────────

/** Top of the tree, expandable one level — enough to see the shape without leaving the page. */
function KnowledgeBaseSection({ projectId, projectSlug }: { projectId: string | undefined; projectSlug: string }) {
  const { data: tree, error, loading } = useProjectResource<KbTree>(projectId, loadTree);
  const [expanded, setExpanded] = useState<string | null>(null);

  const top = (tree?.nodes ?? []).slice(0, 6);

  return (
    <Section title="Knowledge base" action={<HeaderLink to={`/p/${projectSlug}/kb`}>Open</HeaderLink>}>
      {top.length === 0 ? (
        <Placeholder
          loading={loading}
          error={error}
          // Points at the panel above rather than describing the vacuum: on an empty project this
          // section and that panel are on screen together, and one of them should say what to do.
          empty="Nothing written yet — drop a document or paste notes above and the agent writes the first pages."
        />
      ) : (
        top.map((node) => (
          <KbRow
            key={node.id}
            node={node}
            projectSlug={projectSlug}
            open={expanded === node.id}
            onToggle={() => setExpanded(expanded === node.id ? null : node.id)}
          />
        ))
      )}

      {tree && tree.nodes.length > top.length && (
        <Row className="text-[11px] text-ink-faint">
          <Link to={`/p/${projectSlug}/kb`} className="text-orange-500 hover:text-orange-600">
            {tree.nodes.length - top.length} more at the top level — open the knowledge base
          </Link>
        </Row>
      )}
    </Section>
  );
}

/**
 * How many children one expanded row shows. A folder here holds 19 pages, and spilling all of
 * them into an overview panel buries every section under it — past this, the knowledge base
 * itself is the right place to look.
 */
const MAX_CHILDREN_SHOWN = 5;

function KbRow({
  node,
  projectSlug,
  open,
  onToggle,
}: {
  node: KbTreeNode;
  projectSlug: string;
  open: boolean;
  onToggle: () => void;
}) {
  const hasChildren = node.children.length > 0;
  const shown = node.children.slice(0, MAX_CHILDREN_SHOWN);
  const hidden = node.children.length - shown.length;

  return (
    <div>
      <Row>
        {node.kind === 'FOLDER' ? (
          <Folder size={14} className="text-ink-faint flex-shrink-0" />
        ) : (
          <FileText size={14} className="text-ink-faint flex-shrink-0" />
        )}

        <Link
          to={`/p/${projectSlug}/kb/${node.path}`}
          className="min-w-0 truncate hover:text-orange-600 transition-colors"
        >
          {node.title}
        </Link>

        {hasChildren && (
          <button
            onClick={onToggle}
            className="text-ink-faint hover:text-ink-muted transition-colors flex-shrink-0"
            aria-label={open ? `Collapse ${node.title}` : `Expand ${node.title}`}
          >
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        )}

        {node.pendingChanges > 0 && (
          <Badge variant="warning">{node.pendingChanges} to review</Badge>
        )}

        <Meta>{timeAgo(node.updatedAt)}</Meta>
      </Row>

      {open && (
        <>
          {shown.map((child) => (
            <Row key={child.id} className="pl-11 py-2 text-xs text-ink-muted border-t border-border-subtle">
              <Link
                to={`/p/${projectSlug}/kb/${child.path}`}
                className="truncate hover:text-orange-600 transition-colors"
              >
                {child.title}
              </Link>
              {child.childCount > 0 && (
                <span className="text-ink-faint flex-shrink-0">+{child.childCount}</span>
              )}
              <Meta>{timeAgo(child.updatedAt)}</Meta>
            </Row>
          ))}

          {hidden > 0 && (
            <Row className="pl-11 py-2 text-[11px] border-t border-border-subtle">
              <Link
                to={`/p/${projectSlug}/kb/${node.path}`}
                className="text-orange-500 hover:text-orange-600"
              >
                {hidden} more in {node.title}
              </Link>
            </Row>
          )}
        </>
      )}
    </div>
  );
}

// ─── Activity ──────────────────────────────────────────────────────────────

const RUN_POLL_MS = 4000;

/**
 * What this project was handed, part by part — the run drawer's "What it was given" record,
 * scoped to this project.
 *
 * A run row says a batch was absorbed; the placement record says which spans of it this project
 * actually received, and that cut exists nowhere else. So the rows here are those parts: for a
 * routed batch, only what the ingestor assigned to this project; for a pinned run, the whole
 * items, which were never cut. A routed run still deciding shows as a single pulsing row on the
 * project it was received at.
 *
 * Polls only while something is in flight, and stops the moment nothing is.
 */
function ActivitySection({ projectId }: { projectId: string | undefined }) {
  const { data: parts, error, loading, reload } = useProjectResource<KbGivenPart[]>(projectId, loadAbsorbed);
  const sourceLabels = useSourceLabels();

  const hasLive = (parts ?? []).some((part) => part.live);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!hasLive) return;
    pollRef.current = window.setInterval(reload, RUN_POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasLive, reload]);

  const rows = parts ?? [];

  return (
    <Section title="Activity" action={<HeaderLink to="/feed">Feed</HeaderLink>}>
      {rows.length === 0 ? (
        <Placeholder loading={loading} error={error} empty="Nothing has been handed to this project yet." />
      ) : (
        rows.map((part) => (
          <GivenPartRow key={part.id} part={part} projectId={projectId!} sourceLabels={sourceLabels} />
        ))
      )}
    </Section>
  );
}

/**
 * Where a part sits inside the arrival it was cut from.
 *
 * Client-side on purpose: a part is a verbatim slice of the source (spans are sliced, never
 * rewritten — citations depend on that), so its excerpt matches the original exactly and no
 * server round trip is needed. A non-contiguous part is stitched from several spans and only
 * matches up to its first seam, so a shrinking prefix anchors at least the start of the material.
 */
function locatePart(content: string, excerpt: string): { start: number; end: number } | null {
  if (!excerpt) return null;
  const at = content.indexOf(excerpt);
  if (at !== -1) return { start: at, end: at + excerpt.length };
  for (const len of [400, 160, 60]) {
    const prefix = excerpt.slice(0, len).trimEnd();
    if (prefix.length < 20) break;
    const found = content.indexOf(prefix);
    if (found !== -1) return { start: found, end: found + prefix.length };
  }
  return null;
}

/**
 * The raw arrival with the part highlighted in place — the same treatment the citation-evidence
 * drawer gives a quoted span, because "where did this come from" is the same question. Scrolled to
 * the mark inside its own container so a long standup opens at the cut, not at the top.
 */
function OriginalWithHighlight({
  content,
  excerpt,
  ranges,
}: {
  content: string;
  excerpt: string;
  /**
   * The part's exact place in the original, one range per passage, from the run's recorded cut. The
   * excerpt search below is only the fallback: the excerpt is capped at 2,000 characters and stops at
   * a part's first seam, so it lit up 2,000 of a 6,351-character part and 59 of a four-passage one.
   */
  ranges: Array<{ start: number; end: number }> | null;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLElement>(null);
  const located = locatePart(content, excerpt);
  const marks = (ranges && ranges.length > 0 ? ranges : located ? [located] : [])
    .filter((range) => range.start >= 0 && range.end <= content.length && range.end > range.start)
    .sort((a, b) => a.start - b.start);

  useEffect(() => {
    const box = boxRef.current;
    const mark = markRef.current;
    if (!box || !mark) return;
    box.scrollTop = Math.max(0, mark.offsetTop - box.clientHeight / 2);
  }, [content]);

  return (
    <div ref={boxRef} className="max-h-[28rem] overflow-y-auto rounded-md border border-border-subtle bg-surface-subtle">
      <pre className="text-[11px] leading-relaxed whitespace-pre-wrap break-words p-3 font-sans text-ink-secondary">
        {marks.length > 0
          ? marks.map((range, index) => (
              <span key={range.start}>
                {content.slice(index === 0 ? 0 : marks[index - 1]!.end, range.start)}
                <mark
                  ref={index === 0 ? markRef : undefined}
                  className="bg-orange-200 dark:bg-orange-900/70 text-ink rounded px-0.5"
                >
                  {content.slice(range.start, range.end)}
                </mark>
                {index === marks.length - 1 && content.slice(range.end)}
              </span>
            ))
          : content}
      </pre>
    </div>
  );
}

/**
 * One handed-over part: a header line that toggles open exactly the text this project was given —
 * the same bounded excerpt, expander and truncation note the run drawer's placement panel shows —
 * with the way out pointing at the original note when the source has one, and at the run otherwise.
 */
function GivenPartRow({
  part,
  projectId,
  sourceLabels,
}: {
  part: KbGivenPart;
  /** This dashboard's project — the destination the part was handed to. */
  projectId: string;
  sourceLabels: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  /** The whole arrival, fetched once on demand — reading the original beside the cut. */
  const [original, setOriginal] = useState<string | null>(null);
  /** Where the part sits in the original, from the run's recorded cut — null until fetched. */
  const [ranges, setRanges] = useState<Array<{ start: number; end: number }> | null>(null);
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [originalLoading, setOriginalLoading] = useState(false);
  const [originalError, setOriginalError] = useState<string | null>(null);
  const Icon = SOURCE_ICONS[part.sourceType] ?? FileText;

  const toggleOriginal = async () => {
    if (showingOriginal) {
      setShowingOriginal(false);
      return;
    }
    setShowingOriginal(true);
    if (original !== null || !part.feedItemId) return;
    setOriginalLoading(true);
    setOriginalError(null);
    try {
      const [item, located] = await Promise.all([
        api.feed.get(part.feedItemId),
        part.partItem !== null
          ? api.kb.runPartText(projectId, part.runId, { destination: projectId, item: part.partItem }).catch(() => null)
          : Promise.resolve(null),
      ]);
      setRanges(located?.ranges ?? null);
      setOriginal(item.content);
    } catch (e) {
      setOriginalError(e instanceof Error ? e.message : String(e));
      setShowingOriginal(false);
    } finally {
      setOriginalLoading(false);
    }
  };

  // The part itself is always shown whole now, so the original is only worth offering where the part
  // is a cut from something larger — a routed part, which can be lit up inside its arrival.
  const hasMoreThanPart = part.feedItemId !== null && part.itemIndex !== null;

  if (part.deciding) {
    return (
      <Row className="bg-info-bg/40">
        <BreathingDot size={9} className="flex-shrink-0" />
        {/* A run still deciding has no parts yet, so its title *is* the run's own label — a batch's
            composed line included, whose channel ids resolve here like anywhere else. */}
        <span className="min-w-0 truncate text-ink">
          {formatRunLabel(part.title?.trim(), sourceLabels) || formatSource(part.source, sourceLabels)}
        </span>
        <span className="text-[11px] text-ink-muted truncate">deciding where this belongs…</span>
        <Meta>{formatChars(part.chars)}</Meta>
      </Row>
    );
  }

  return (
    <div className={cn(part.live && 'bg-info-bg/40')}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left hover:bg-surface-subtle transition-colors"
      >
        {part.live ? (
          <BreathingDot size={9} className="flex-shrink-0" />
        ) : (
          <SourceChip icon={Icon} label={part.sourceType.toLowerCase().replace('_', ' ')} />
        )}

        {/* The same dot and fork the review queue uses, wrapped so the explanation has
            somewhere to live — an svg takes no title. */}
        <span
          className="flex items-center flex-shrink-0"
          title={
            part.triage
              ? 'Auto-routed — the ingestor assigned this part of the arrival to this project'
              : 'Pinned — this arrived addressed to this project, so nothing was routed'
          }
        >
          <PlacementMark triage={part.triage} />
        </span>

        <span className="min-w-0 truncate text-ink">
          {part.title?.trim() || formatSource(part.source, sourceLabels)}
        </span>

        {/* Which arrival of the batch the part was cut from — the drawer's "item 3" marker. */}
        {part.itemIndex !== null && (
          <span className="text-[10px] text-ink-faint flex-shrink-0">item {part.itemIndex}</span>
        )}

        {part.failed && <Badge variant="danger">failed</Badge>}

        <Meta>
          {formatChars(part.chars)} · {part.live ? 'absorbing…' : timeAgo(part.at)}
        </Meta>
        {open ? (
          <ChevronUp size={12} className="text-ink-faint flex-shrink-0" />
        ) : (
          <ChevronDown size={12} className="text-ink-faint flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-1.5">
          {showingOriginal && original !== null ? (
            part.itemIndex !== null ? (
              /* The whole arrival with this part lit up inside it — the citation drawer's raw-source
                 treatment, since "where was this cut from" is the same question as "where was this
                 quoted from". */
              <OriginalWithHighlight content={original} excerpt={part.excerpt} ranges={ranges} />
            ) : (
              /* A pinned item was never cut, so there is nothing to highlight — just the whole
                 thing where the excerpt stopped short. */
              <pre className="text-[11px] text-ink-secondary leading-snug whitespace-pre-wrap break-words font-sans max-h-[28rem] overflow-y-auto rounded-md border border-border-subtle bg-surface-subtle px-3 py-2">
                {original}
              </pre>
            )
          ) : (
            /* All of it, not an excerpt — the drawer's own treatment, because checking the cut and
               quoting from it is what this panel is opened for. */
            <PartText
              excerpt={part.excerpt}
              chars={part.chars}
              truncated={part.truncated && part.partItem !== null}
              projectId={projectId}
              runId={part.runId}
              part={{ destination: projectId, item: part.partItem ?? 0 }}
            />
          )}
          {originalError && <p className="text-[10px] text-danger-text">{originalError}</p>}
          <div className="flex items-center gap-3 flex-wrap">
          {hasMoreThanPart && (
            <button
              onClick={() => void toggleOriginal()}
              disabled={originalLoading}
              className="text-[10px] font-medium text-orange-600 dark:text-orange-400 hover:underline disabled:opacity-60"
            >
              {originalLoading
                ? 'Loading…'
                : showingOriginal
                  ? part.itemIndex !== null
                    ? 'Show only this part'
                    : 'Show the excerpt'
                  : part.itemIndex !== null
                    ? 'Show it in the original item'
                    : 'Show full content'}
            </button>
          )}
          {part.sourceUrl && (
            <a
              href={part.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[10px] font-medium text-ink-faint hover:text-ink transition-colors"
            >
              <ArrowUpRight size={10} />
              Open the original {part.sourceType === 'GRANOLA' ? 'note in Granola' : 'source'}
            </a>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Files & artifacts ─────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Uploads into this project, newest first. Linked only when the bucket has a public URL. */
function FilesSection({ projectId }: { projectId: string | undefined }) {
  const { data: uploads, error, loading } = useProjectResource<KbUpload[]>(projectId, loadUploads);
  const rows = uploads ?? [];

  return (
    <Section title="Files & artifacts" action={<HeaderLink to="/add"><Upload size={11} /> Add a file</HeaderLink>}>
      {rows.length === 0 ? (
        <Placeholder loading={loading} error={error} empty="No files have been uploaded here yet." />
      ) : (
        rows.map((file) => (
          <Row key={file.id}>
            <FileText size={14} className="text-ink-faint flex-shrink-0" />

            {file.storageUrl ? (
              <a
                href={file.storageUrl}
                target="_blank"
                rel="noreferrer"
                className="min-w-0 truncate hover:text-orange-600 transition-colors"
              >
                {file.fileName}
              </a>
            ) : (
              <span className="min-w-0 truncate">{file.fileName}</span>
            )}

            {file.status === 'FAILED' && (
              <Badge variant="danger" title={file.errorMessage ?? undefined}>
                failed
              </Badge>
            )}
            {file.status === 'UPLOADED' && <Badge variant="muted">not read yet</Badge>}
            {/* The file lives at the organization root — the ingestion run chose this project as a
                destination. Without the tag a routed file is indistinguishable from one somebody
                filed here by hand, and clicking through to the run is how "what did it become" is
                answered. */}
            {file.routed &&
              (file.runId ? (
                <Link
                  to={`/activity?run=${file.runId}`}
                  title="Received at the organization level; the ingestion run filed content from it in this project. Open the run."
                >
                  <Badge variant="info">routed here</Badge>
                </Link>
              ) : (
                <Badge variant="info" title="Received at the organization level; the ingestion run filed content from it in this project.">
                  routed here
                </Badge>
              ))}

            <Meta>
              {formatBytes(file.sizeBytes)} · {file.uploadedBy} · {timeAgo(file.createdAt)}
            </Meta>
          </Row>
        ))
      )}
    </Section>
  );
}

// ─── Automations ───────────────────────────────────────────────────────────

/** The tail of an automation's history, as one line. */
function lastRunLabel(automation: AutomationWithRuns): string {
  const run = automation.runs[0];
  if (!run) return 'never run';
  if (run.status === 'FAILED') return `failed ${timeAgo(run.queuedAt)}`;
  if (run.status === 'QUEUED' || run.status === 'PROCESSING') return 'running now';
  return `ran ${timeAgo(run.finishedAt ?? run.queuedAt)}`;
}

function AutomationsSection({ projectId }: { projectId: string | undefined }) {
  const { data: automations, error, loading } = useProjectResource<AutomationWithRuns[]>(
    projectId,
    loadAutomations,
  );
  const rows = automations ?? [];

  return (
    <Section title="Automations" action={<HeaderLink to="/automations"><Bot size={11} /> Manage</HeaderLink>}>
      {rows.length === 0 ? (
        <Placeholder
          loading={loading}
          error={error}
          empty="No automations run for this project yet."
        />
      ) : (
        rows.map((automation) => {
          const Icon = triggerIcon(automation.trigger as AutomationTrigger) ?? CalendarClock;

          return (
            <Row key={automation.id}>
              <Icon size={13} className="text-ink-faint flex-shrink-0" />

              {/* Deep-links to this one automation, expanded — the org-wide list is long enough
                  that landing at its top would mean hunting for the row you clicked. */}
              <Link
                to={`/automations?automation=${automation.id}`}
                className="min-w-0 truncate hover:text-orange-600 transition-colors"
              >
                {automation.name}
              </Link>

              {!automation.enabled && <Badge variant="muted">paused</Badge>}

              <span className="ml-auto flex items-center gap-2 flex-shrink-0">
                <span className="text-xs text-ink-faint hidden sm:inline">
                  {triggerSummary(automation)}
                </span>
                <span className="text-xs text-ink-muted">{lastRunLabel(automation)}</span>
              </span>
            </Row>
          );
        })
      )}
    </Section>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { project, projectSlug } = useCurrentProject();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: stats, reload: reloadStats } = useProjectResource(project?.id, loadStats);

  /**
   * `?start=1` — arrived here meaning to put something in, which is how the create flow hands over.
   *
   * Consumed once and stripped, so a reload or a back doesn't re-open a panel someone has closed.
   */
  const [startRequested, setStartRequested] = useState(false);
  const [failedUploads, setFailedUploads] = useState<string[]>([]);
  useEffect(() => {
    if (!searchParams.has('start')) return;
    setStartRequested(true);
    const failed = searchParams.get('failed');
    if (failed) setFailedUploads(failed.split(',').filter(Boolean));
    const next = new URLSearchParams(searchParams);
    next.delete('start');
    next.delete('failed');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /**
   * Files dropped anywhere on the page, handed to the starter panel.
   *
   * The panel has its own dropzone; this is for the other 90% of the page, because someone dragging
   * a document at a project is aiming at the project, not at a particular rectangle. A fresh array
   * per drop is what the panel keys its effect on.
   */
  const [dropped, setDropped] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);

  // Bumped when a run finishes so the starter's completion refreshes the stats and the tree beneath
  // it — the page has visibly changed by then, and it is the first thing someone looks at.
  const [version, setVersion] = useState(0);
  const onIngested = useCallback(() => {
    reloadStats();
    setVersion((v) => v + 1);
  }, [reloadStats]);

  // Unknown while loading, and "empty" is a claim worth being sure of: guessing it from a null
  // response would flash the empty-project panel at every project on every page load.
  const isEmpty = stats !== null && stats.pageCount === 0;

  return (
    <Layout
      title={project?.name ?? 'Project'}
      // The same mark the nav tree shows — image, chosen emoji, or the tinted initial — so the
      // overview is recognisably the page of the row that was clicked.
      leading={project ? <ProjectMark project={project} size="lg" /> : undefined}
      subtitle={project?.description ?? undefined}
      narrow
    >
      <div
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            setDragging(true);
          }
        }}
        onDragLeave={(e) => {
          // Only when the pointer actually leaves the page, not on every child boundary it crosses.
          if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
          setDragging(false);
        }}
        onDrop={(e) => {
          const files = Array.from(e.dataTransfer.files ?? []);
          if (files.length === 0) return;
          e.preventDefault();
          setDragging(false);
          setDropped(files);
        }}
        className={cn(
          'rounded-xl transition-colors',
          dragging && 'ring-2 ring-orange-400 ring-offset-4 ring-offset-surface',
        )}
      >
        {dragging && (
          <p className="mb-3 text-xs text-orange-600 dark:text-orange-400 font-medium">
            Drop to add to {project?.name ?? 'this project'}
          </p>
        )}

        <StatsRow projectId={project?.id} projectSlug={projectSlug} />

        <div className="space-y-4">
          {/* First, above everything: on an empty project it is the only thing worth doing, and on a
              full one it is still the action people come here to take. */}
          {project && (
            <ProjectStarter
              projectId={project.id}
              projectName={project.name}
              isEmpty={isEmpty}
              startOpen={startRequested}
              failedUploads={failedUploads}
              incomingFiles={dropped}
              onIngested={onIngested}
            />
          )}

          <KnowledgeBaseSection key={version} projectId={project?.id} projectSlug={projectSlug} />
          <ActivitySection key={`activity-${version}`} projectId={project?.id} />
          <FilesSection key={`files-${version}`} projectId={project?.id} />
          <AutomationsSection projectId={project?.id} />
        </div>
      </div>
    </Layout>
  );
}
